<template>
  <DialogRoot v-model:open="isOpen" modal>
    <DialogTrigger as-child>
      <slot name="activator" />
    </DialogTrigger>

    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-modal bg-[#000000]/20"
        :style="overlayStyle"
      />
      <DialogContent
        as-child
        :aria-describedby="undefined"
        :style="contentStyle"
        @pointer-down-outside="onPointerDownOutside"
        @escape-key-down="onEscapeKeyDown"
      >
        <div v-if="slots.content" :class="modalClass">
          <DialogClose v-if="props.closable" class="absolute top-4 right-4">
            <Icon :icon="CloseIcon" class="text-line" />
          </DialogClose>
          <slot name="content" :close-modal="closeModal" />
        </div>

        <component
          :is="props.asForm ? Form : 'div'"
          v-else
          :class="modalClass"
          @submit="onSubmit"
        >
          <div v-if="slots.header" :class="headerClass">
            <slot name="header" :close-modal="closeModal" />
          </div>
          <DialogTitle
            v-else
            :class="headerClass"
            class="text-center text-xl text-on-surface py-5 sm:py-7 font-bold leading-none border-b"
          >
            {{ props.title }}
          </DialogTitle>

          <div v-if="slots.body" class="flex-1 px-6 sm:px-8">
            <slot name="body" />
          </div>

          <div v-if="slots.actions" :class="actionsClass">
            <slot name="actions" :close-modal="closeModal" />
          </div>
          <div
            v-else
            :class="actionsClass"
            class="flex justify-between py-5 px-4 sm:px-12 border-t"
          >
            <Btn :size="BtnSize.LG" @click="closeModal">
              {{ $t('common.cancel') }}
            </Btn>
            <Btn
              type="submit"
              :color="BtnColor.PRIMARY"
              :variant="BtnVariant.FILLED"
              :size="BtnSize.LG"
            >
              {{ props.submitButtonText }}
            </Btn>
          </div>
        </component>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from 'reka-ui';
import { Form } from 'vee-validate';
import {
  ModalHeight,
  ModalPosition,
  ModalShape,
  ModalSize,
} from './modal.constants';
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { Icon } from '~/components/icon';
import CloseIcon from '~/assets/icons/close.svg?component';

type Props = {
  asForm?: boolean;
  closable?: boolean;
  persistent?: boolean;
  fullHeight?: boolean;
  withDividers?: boolean;
  disableFocusTrap?: boolean;
  size?: ModalSize;
  shape?: ModalShape;
  position?: ModalPosition;
  height?: ModalHeight;
  title?: string;
  submitButtonText?: string;
  zIndex?: number;
};

const props = withDefaults(defineProps<Props>(), {
  size: ModalSize.MD,
  shape: ModalShape.ROUNDED,
  position: ModalPosition.CENTER,
  height: ModalHeight.MD,
});

const emit = defineEmits<{
  close: [];
  submit: [];
}>();

const isOpen = defineModel<boolean>('open', { default: false });

const slots = defineSlots<{
  activator(): unknown;
  content(props: { closeModal: () => void }): unknown;
  header(props: { closeModal: () => void }): unknown;
  body(): unknown;
  actions(props: { closeModal: () => void }): unknown;
}>();

const classByModalPosition: Record<ModalPosition, string> = {
  [ModalPosition.LEFT]: 'left-0',
  [ModalPosition.CENTER]: 'left-1/2 -translate-x-1/2',
  [ModalPosition.RIGHT]: 'right-0',
};

const classByModalSize: Record<ModalSize, string> = {
  [ModalSize.SM]: 'w-[27.5rem]',
  [ModalSize.MD]: 'w-[36rem]',
  [ModalSize.LG]: 'w-[58.5rem]',
  [ModalSize.XL]: 'w-[70rem]',
  [ModalSize.XXL]: 'w-[93rem]',
  [ModalSize.FULL]: 'w-full',
};

const classByModalShape: Record<ModalShape, string> = {
  [ModalShape.ROUNDED]: 'rounded-none md:rounded-2xl',
  [ModalShape.SQUARE]: 'rounded-none',
};

const classByModalHeight: Record<ModalHeight, string> = {
  [ModalHeight.XS]: 'h-[20rem]',
  [ModalHeight.SM]: 'h-[27.5rem]',
  [ModalHeight.MD]: 'h-[36rem]',
  [ModalHeight.LG]: 'h-[43.5rem]',
  [ModalHeight.XL]: 'h-[49rem]',
  [ModalHeight.XXL]: 'h-[52rem]',
};

const modalClass = computed(() => [
  'fixed z-modal top-1/2 -translate-y-1/2 bg-surface overflow-y-auto max-w-full flex flex-col',
  classByModalSize[props.size],
  classByModalPosition[props.position],
  classByModalShape[props.shape],
  classByModalHeight[props.height],
  {
    'h-full': props.fullHeight,
    'md:max-w-[calc(100%-32px)]': props.size !== ModalSize.FULL,
  },
]);

const overlayStyle = computed(() => ({
  ...(props.zIndex && { zIndex: props.zIndex }),
}));

const contentStyle = computed(() => ({
  ...(props.zIndex && { zIndex: props.zIndex }),
}));

const headerClass = 'sticky top-0 bg-inherit z-10';
const actionsClass = 'sticky bottom-0 bg-inherit z-10';

const closeModal = (): void => {
  isOpen.value = false;
  emit('close');
};

const onSubmit = () => {
  emit('submit');
  closeModal();
};

const onPointerDownOutside = (event: Event) => {
  if (props.persistent) {
    event.preventDefault();
  }
};

const onEscapeKeyDown = (event: KeyboardEvent) => {
  if (props.persistent) {
    event.preventDefault();
  }
};
</script>
